import type { AvailabilityReportItem } from '@/types';
import type { AvailabilityFilters } from '@/stores/availability/availability-store';
import type { ColumnPinSide, ColumnConfig } from '@/stores/availability/availability-column-store';
import { AVAILABILITY_FILTERABLE_COLUMN_IDS } from '@/utils/availability/filter-column-mapping';
import { formatBenchmarkFee } from '@/utils/availability/format';
import { useTableContextMenus } from '@/hooks/useTableContextMenus';
import type { ColumnDef } from '@tanstack/react-table';

/** Map column IDs to their filter array key for "Filter by value". */
const COLUMN_ID_TO_FILTER_ARRAY_KEY: Partial<Record<string, keyof AvailabilityFilters>> = {
  ca: 'caStatuses',
  counterparties: 'counterparties',
};

/** Map column IDs to their min/max filter keys for numeric "filter by value". */
const COLUMN_ID_TO_NUMERIC_FILTER_KEYS: Partial<Record<string, { min: keyof AvailabilityFilters; max: keyof AvailabilityFilters }>> = {
  quantity: { min: 'quantityMin', max: 'quantityMax' },
};

const SKIP_ARRAY_FILTER_COLUMNS = new Set(['counterparties']);

const getCellDisplayValue = (columnId: string, item: AvailabilityReportItem): string => {
  if (columnId === 'counterparties') {
    return item.counterparties.map((cp) => `${cp.name}: ${cp.quantity}`).join(', ');
  }
  if (columnId === 'benchmark_fee') {
    // Context-menu copies must match the rendered cell: two decimals (LR-599).
    return formatBenchmarkFee(item.benchmark_fee);
  }
  if (columnId === 'transfers') {
    return item.pending_transfers > 0 ? `Out: ${item.pending_transfers}` : '-';
  }
  const value = item[columnId as keyof AvailabilityReportItem];
  if (value === null || value === undefined) return '';
  return String(value);
};

interface UseAvailabilityContextMenusParams {
  columnConfig: ColumnConfig[];
  columns: ColumnDef<AvailabilityReportItem, unknown>[];
  sorting: { id: string; desc: boolean }[];
  setSorting: (sorting: { id: string; desc: boolean }[]) => void;
  getPinState: (columnId: string) => ColumnPinSide;
  pinColumn: (columnId: string, side: ColumnPinSide) => void;
  toggleColumnVisibility: (columnId: string) => void;
  handleFilterColumnClick: (columnId: string) => void;
  availabilityFilters: AvailabilityFilters;
  setAvailabilityFilters: (filters: AvailabilityFilters) => void;
  sortedDataRef: React.MutableRefObject<AvailabilityReportItem[]>;
  colIds: string[];
  /** External selection state from useCellSelection */
  selectedCellsForCopy: () => Set<string>;
  selectedCellCount: () => number;
  selectColumn: (colIndex: number, additive: boolean) => void;
  deselectColumn: (colIndex: number) => void;
  isColumnFullySelected: (colIndex: number) => boolean;
  colIdToIndex: Map<string, number>;
}

export function useAvailabilityContextMenus({
  columnConfig,
  columns,
  sorting,
  setSorting,
  getPinState,
  pinColumn,
  toggleColumnVisibility,
  handleFilterColumnClick,
  availabilityFilters,
  setAvailabilityFilters,
  sortedDataRef,
  colIds,
  selectedCellsForCopy,
  selectedCellCount,
  selectColumn,
  deselectColumn,
  isColumnFullySelected,
  colIdToIndex,
}: UseAvailabilityContextMenusParams) {
  return useTableContextMenus<AvailabilityReportItem, AvailabilityFilters>({
    columnConfig,
    columns,
    sorting,
    setSorting,
    getPinState,
    pinColumn,
    toggleColumnVisibility,
    handleFilterColumnClick,
    sortedDataRef,
    filterConfig: {
      filterableColumnIds: AVAILABILITY_FILTERABLE_COLUMN_IDS,
      arrayFilterMap: COLUMN_ID_TO_FILTER_ARRAY_KEY,
      numericFilterMap: COLUMN_ID_TO_NUMERIC_FILTER_KEYS,
      skipArrayFilterColumns: SKIP_ARRAY_FILTER_COLUMNS,
      getCellDisplayValue,
      filters: availabilityFilters,
      setFilters: setAvailabilityFilters,
    },
    colIds,
    selectedCellsForCopy,
    selectedCellCount,
    selectColumn,
    deselectColumn,
    isColumnFullySelected,
    colIdToIndex,
    tableName: 'availability',
  });
}
